import { useAuthStore } from '../model/stores/authStore';
import { OBJECT_URL, MEDIA_URL } from '../model/constants';
import { getFreshJwt } from './authClient';

// ── JWT helpers ───────────────────────────────────────────────────────────────

/** JWT für Write-Operationen — frisch und nicht abgelaufen. */
async function getJwt(): Promise<string | null> {
  try { return await getFreshJwt(); } catch { return null; }
}

/** Gibt die User-ID zurück — bevorzugt den gespeicherten Wert aus dem Store. */
export function getJwtUserId(): string {
  const { userId, jwt } = useAuthStore.getState();
  if (userId) return userId;
  // Fallback: ID-Claim aus JWT parsen (falls userId noch nicht persistiert ist)
  try {
    if (!jwt) return '';
    const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const id = String(payload.id ?? payload.userId ?? payload.sub ?? '');
    return id;
  } catch {
    return '';
  }
}

// ── ObjectService ─────────────────────────────────────────────────────────────

export interface ObjectDoc {
  _id: string;
  data: Record<string, unknown>;
  refs?: Record<string, string>;
  app?: string;
  isPublic?: boolean;
}

/** Fetch mit optionalem JWT — für Reads genügt es wenn vorhanden, für Writes ist es Pflicht. */
async function objFetch<T>(path: string, options: RequestInit = {}, jwt?: string | null): Promise<T> {
  const authHeaders: Record<string, string> = jwt ? { Authorization: `Bearer ${jwt}` } : {};
  const res = await fetch(`${OBJECT_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...(options.headers as Record<string, string> | undefined),
    },
  });
  const data = await res.json() as Record<string, unknown>;
  if (!res.ok) throw new Error(String(data.error ?? data.detail ?? 'ObjectService-Fehler'));
  return data as T;
}

/** Liest Objekte — funktioniert auch ohne JWT (public reads). */
export async function listObjects(
  collection: string,
  params?: Record<string, string>,
): Promise<ObjectDoc[]> {
  const jwt = await getJwt(); // optional aber frisch falls vorhanden
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  const result = await objFetch<unknown>(`/objects/${collection}${qs}`, {}, jwt);
  if (Array.isArray(result)) return result as ObjectDoc[];
  const r = result as Record<string, unknown>;
  if (Array.isArray(r.objects)) return r.objects as ObjectDoc[];
  if (Array.isArray(r.items))   return r.items   as ObjectDoc[];
  if (Array.isArray(r.data))    return r.data    as ObjectDoc[];
  return [];
}

export async function createObject(
  collection: string,
  data: Record<string, unknown>,
  refs?: Record<string, string>,
  app = 'VirtualOffice',
  isPublic = true,
): Promise<ObjectDoc> {
  const jwt = await getJwt();
  return objFetch<ObjectDoc>(`/objects/${collection}`, {
    method: 'POST',
    body: JSON.stringify({ data, refs, app, isPublic }),
  }, jwt);
}

export async function putObject(
  collection: string,
  id: string,
  data: Record<string, unknown>,
  refs?: Record<string, string>,
  app = 'VirtualOffice',
  isPublic = true,
): Promise<ObjectDoc> {
  const jwt = await getJwt();
  return objFetch<ObjectDoc>(`/objects/${collection}/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ data, refs, app, isPublic }),
  }, jwt);
}

export async function patchObject(
  collection: string,
  id: string,
  data?: Record<string, unknown>,
  refs?: Record<string, string>,
): Promise<ObjectDoc> {
  const jwt = await getJwt();
  return objFetch<ObjectDoc>(`/objects/${collection}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ data, refs, merge: true }),
  }, jwt);
}

export async function deleteObject(collection: string, id: string): Promise<void> {
  const jwt = await getJwt();
  await objFetch<unknown>(`/objects/${collection}/${id}`, { method: 'DELETE' }, jwt);
}

// ── Message-Favoriten (vo_message_favorites) ──────────────────────────────────

export async function loadFavorites(
  myId: string,
): Promise<{ docId: string | null; favorites: string[] }> {
  try {
    const docs = await listObjects('vo_message_favorites', {
      'ref[userId]': myId,
      app: 'VirtualOffice',
    });
    if (!docs.length) return { docId: null, favorites: [] };
    const favs = docs[0].data.favorites;
    return {
      docId: docs[0]._id,
      favorites: Array.isArray(favs) ? (favs as string[]) : [],
    };
  } catch {
    return { docId: null, favorites: [] };
  }
}

export async function saveFavorites(
  myId: string,
  favorites: string[],
  docId: string | null,
): Promise<string | null> {
  try {
    const doc = docId
      ? await putObject(
          'vo_message_favorites', docId,
          { favorites }, { userId: myId }, 'VirtualOffice', false,
        )
      : await createObject(
          'vo_message_favorites',
          { favorites }, { userId: myId }, 'VirtualOffice', false,
        );
    return doc._id;
  } catch {
    return docId;
  }
}

// ── MediaService ──────────────────────────────────────────────────────────────

export async function uploadMedia(
  file: File,
  appName = 'VirtualOffice',
  folder?: string,
  name?: string,
  description?: string,
): Promise<{ url: string; id: string }> {
  const jwt = await getJwt();
  const form = new FormData();
  form.append('file', file);
  form.append('app_name', appName);
  if (folder)      form.append('folder', folder);
  if (name)        form.append('name', name);
  if (description) form.append('description', description);

  const headers: Record<string, string> = {};
  if (jwt) headers['Authorization'] = `Bearer ${jwt}`;

  const res = await fetch(`${MEDIA_URL}/upload`, {
    method: 'POST',
    headers,
    body: form,
  });
  const data = await res.json() as Record<string, unknown>;
  if (!res.ok) throw new Error(String(data.error ?? data.detail ?? 'Upload fehlgeschlagen'));
  return {
    url: String(data.url ?? ''),
    id:  String(data._id ?? data.id ?? ''),
  };
}

export interface MediaFile {
  id: string;
  url: string;
  name?: string;
}

export async function browseMedia(appName: string, folder?: string): Promise<MediaFile[]> {
  const jwt = await getJwt();
  const path = folder ? `/browse/${appName}/${folder}` : `/browse/${appName}`;
  const headers: Record<string, string> = {};
  if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
  const res = await fetch(`${MEDIA_URL}${path}`, { headers });
  if (!res.ok) return [];
  const data = await res.json() as Record<string, unknown>;
  const files = Array.isArray(data.files) ? (data.files as Record<string, unknown>[]) : [];
  return files.map((f) => ({
    id:   String(f._id ?? f.id ?? ''),
    url:  String(f.url ?? ''),
    name: f.name ? String(f.name) : undefined,
  }));
}

export async function deleteMedia(id: string): Promise<void> {
  const jwt = await getJwt();
  const headers: Record<string, string> = {};
  if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
  await fetch(`${MEDIA_URL}/media/${id}`, { method: 'DELETE', headers });
}
